<template>
  <Sheet>
    <Sheet.Body class="px-4! pt-5">
      <ShieldIcon class="mx-auto mb-2.5 size-6 stroke-gray-800" />

      <h2
        class="mb-[18px] text-center text-[20px] font-medium leading-[26px] text-gray-800"
      >
        Confirmation of right to register work
      </h2>

      <p class="px-2 text-[11px] leading-[17px] text-gray-800">
        By continuing, I represent and warrant that I own or control the
        copyright (or exclusive rights) in the work(s) submitted, or I am duly
        authorized to submit them for the rights holder, and that Dacr may rely
        on this representation. Submitting work without rights or providing
        false information may be unlawful (including fraud) and may also expose
        me to civil and criminal liability under applicable law.
      </p>

      <div class="my-[14px] h-px w-full bg-[#DFDFDF]" />

      <div
        class="mb-2 rounded-xl border border-[#EEEDEC] bg-[#F6F6F6] px-[13px] pt-[7px] pb-[9px]"
      >
        <div class="mb-2 flex items-center gap-1">
          <span class="text-[13px] font-medium leading-[17px] text-gray-800">
            Selected jurisdiction to be registered
          </span>
          <InfoSmallIcon class="size-3.5 text-gray-400" />
        </div>

        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <Avatar
              v-for="origin in selectedOrigins"
              :key="origin.id"
              :src="origin.flag?.url"
              :size="32"
              class="z-10 -mr-3 border-[3px] border-[#F6F6F6]"
            />
            <button
              type="button"
              class="flex size-8 items-center justify-center rounded-full border border-dashed border-gray-300/50"
              @click="handleChange"
            >
              <AddIcon class="size-3 text-gray-400" />
            </button>
          </div>
          <button
            type="button"
            class="text-link text-[14px]"
            @click="handleChange"
          >
            Change
          </button>
        </div>
      </div>

      <div
        class="flex flex-col gap-y-1.5 rounded-xl border border-[#EEEDEC] px-[13px] py-[7px]"
      >
        <div class="flex items-center justify-between">
          <span class="text-[13px] leading-[18px] text-gray-600">
            Works being registered
          </span>
          <span class="text-[14px] leading-[18px] text-gray-800">
            {{ workCount }}
          </span>
        </div>

        <div class="flex items-center justify-between">
          <div class="flex items-center gap-x-2">
            <span class="text-[13px] font-medium leading-[18px] text-gray-800">
              Total to be billed
            </span>
            <button
              type="button"
              class="text-link text-[13px]"
              @click="handleDetails"
            >
              Details
            </button>
          </div>
          <span class="text-[17px] font-medium leading-[18px] text-gray-800">
            {{ totalCost }}
          </span>
        </div>
      </div>

      <div class="mt-[14px] mb-5 flex items-start gap-x-2">
        <Checkbox
          v-model="agreeToTerms"
          :class="['mt-px shrink-0', { 'cb-attention': !agreeToTerms }]"
        />
        <p
          class="cursor-pointer text-[12px] leading-[17px] text-gray-600"
          @click="agreeToTerms = !agreeToTerms"
        >
          I represent and warrant I have the rights/authorization to submit
          these work(s), and agree to the
          <a class="text-link" href="/legal/terms" target="_blank" @click.stop
            >Dacr Terms</a
          >,
          <a class="text-link" href="/legal/privacy" target="_blank" @click.stop
            >Privacy Policy</a
          >, and
          <a
            class="text-link"
            href="/legal/payments"
            target="_blank"
            @click.stop
            >Payments Policy</a
          >. All fees are final except where required by law.
        </p>
      </div>

      <div class="flex flex-col items-center gap-2">
        <Button
          variant="primary"
          size="lg"
          class="w-full"
          :disabled="disableButton"
          :loading="loading"
          @click="save"
        >
          Agree, continue
        </Button>
        <button type="button" class="text-link text-[14px]" @click="closeAll()">
          Cancel
        </button>
      </div>
    </Sheet.Body>
  </Sheet>
</template>

<script setup lang="ts">
import { Sheet, Button, Checkbox, Avatar, useSheet } from '@dacr/library'
import { AddIcon, InfoSmallIcon } from '@dacr/icons'
import ShieldIcon from '@/assets/svg/icons/ShieldIcon.svg'
import { openSheet, Sheets } from '@/shared/dialogs/sheets'
import type { File } from '@/shared/graphql/router/graphql'
import { useConfirmRegisterCountries } from './useConfirmRegisterCountries'

const props = defineProps<{
  files: File[]
  originIds: string[]
}>()

const { close, closeAll } = useSheet()

const {
  workCount,
  selectedOrigins,
  estimatedCost,
  totalCost,
  agreeToTerms,
  disableButton,
  loading,
  save,
} = useConfirmRegisterCountries(props.files, props.originIds, closeAll)

const handleChange = () => close()

const handleDetails = () =>
  openSheet(Sheets.UploadPricingDetails, {
    stack: 'cover',
    props: { estimatedCost: estimatedCost.value },
  })
</script>

<style scoped>
.cb-attention :deep(span) {
  border-color: var(--color-green-500);
  box-shadow: 0 0 0 2px
    color-mix(in srgb, var(--color-green-200) 50%, transparent);
}
</style>
